import type { Language } from "@heredajo/schema";

export interface Strings {
  about: string;
  translationLabel: string;
  diariesTitle: string;
  diariesSubtitle: string;
  loadingDiaries: string;
  loadingDiary: (n: number) => string;
  loadingPage: (n: number) => string;
  pagesCount: (n: number) => string;
  noThumbnail: string;
  noTranscription: string;
  noTranslation: string;
  originalIsInTargetLanguage: (lang: string) => string;
  translationFailed: (err: string) => string;
  errorPrefix: string;
  manifestLink: string;
  originalTab: string;
  confidenceLabel: string;
  msPageLabel: string;
  prev: string;
  next: string;
  pageLocator: (slug: string) => string;
  pageOf: (total: number) => string;
  go: string;
  attribution: string;
  attributionDisclaimer: string;
  // About page paragraphs
  aboutTitle: string;
  aboutIntro: string;
  aboutDigitizedBy: string;
  aboutQualityNote: string;
  aboutAttributionFooter: string;
  // Hover labels for editorial marks rendered in the diary text
  markCrossedOut: string;
  markIllegible: string;
  markUncertain: string;
  markInserted: string;
  markArrow: string;
  markDrawing: string;
  authorPageLabel: string;
  // Auth UI
  signInTab: string;
  signUpTab: string;
  signIn: string;
  signUp: string;
  signOut: string;
  email: string;
  password: string;
  passwordConfirm: string;
  newPassword: string;
  verificationCode: string;
  forgotPassword: string;
  resetPassword: string;
  resendCode: string;
  backToSignIn: string;
  signInPrompt: string;
  signUpPrompt: string;
  pendingApprovalTitle: string;
  pendingApprovalBody: string;
  verificationSentTitle: string;
  verificationSentBody: (email: string) => string;
  newPasswordRequiredTitle: string;
  newPasswordRequiredBody: string;
  forgotPasswordPrompt: string;
  forgotPasswordCodeSent: string;
  passwordChangedTitle: string;
  passwordChangedBody: string;
  passwordMinHint: string;
  workingLabel: string;
  signedInAs: (email: string) => string;
}

const STRINGS: Record<Language, Strings> = {
  en: {
    about: "About",
    translationLabel: "Translation:",
    diariesTitle: "Diaries of Henri F. Ellenberger",
    diariesSubtitle:
      "41 handwritten volumes spanning 1940–1989, written in Esperanto and French (with occasional German). Digitized by the Austrian National Library, transcribed and translated using Gemini.",
    loadingDiaries: "Loading diaries…",
    loadingDiary: () => "Loading diary…",
    loadingPage: (n) => `Loading page ${n}…`,
    pagesCount: (n) => `${n} pages`,
    noThumbnail: "no thumbnail",
    noTranscription: "No transcription.",
    noTranslation: "No translation yet.",
    originalIsInTargetLanguage: (lang) => `The original is in ${lang} — see the Original tab.`,
    translationFailed: (err) => `Translation failed: ${err}`,
    errorPrefix: "Error",
    manifestLink: "IIIF manifest ↗",
    originalTab: "Original",
    confidenceLabel: "Transcription confidence",
    msPageLabel: "MS page",
    prev: "Prev",
    next: "Next",
    pageLocator: (slug) => `Diary ${slug}, page`,
    pageOf: (total) => `of ${total}`,
    go: "Go",
    attribution:
      "Image courtesy of the Austrian National Library (Österreichische Nationalbibliothek), Department of Planned Languages.",
    attributionDisclaimer:
      "Transcriptions and translations are machine-generated and may contain errors.",
    aboutTitle: "About this project",
    aboutIntro:
      'Heredajo (Esperanto: heredaĵo, "inheritance") presents the handwritten diaries of Henri F. Ellenberger (1905–1993), Swiss-Canadian psychiatrist and historian of psychiatry, alongside machine-generated transcriptions and translations.',
    aboutDigitizedBy:
      "The 41 diaries span 1940–1989 and are written primarily in Esperanto and French, with occasional German. They were digitized by the Austrian National Library (Österreichische Nationalbibliothek), Department of Planned Languages, and are served via their IIIF endpoints. The original images on this site are streamed directly from onb.ac.at.",
    aboutQualityNote:
      "Transcriptions and translations were produced with Gemini and may contain errors — especially with cursive handwriting, idiosyncratic Esperanto, and pages with crossed-out or pasted material. Confidence ratings and source-language detections on each page record the model's own self-assessment.",
    aboutAttributionFooter:
      "Image attribution: Österreichische Nationalbibliothek, Sammlung für Plansprachen.",
    markCrossedOut: "crossed out by the author",
    markIllegible: "illegible",
    markUncertain: "uncertain reading",
    markInserted: "inserted above the line by the author",
    markArrow: "marked with an arrow by the author",
    markDrawing: "drawing on the page",
    authorPageLabel: "author",
    signInTab: "Sign in",
    signUpTab: "Create account",
    signIn: "Sign in",
    signUp: "Create account",
    signOut: "Sign out",
    email: "Email",
    password: "Password",
    passwordConfirm: "Confirm password",
    newPassword: "New password",
    verificationCode: "Verification code",
    forgotPassword: "Forgot your password?",
    resetPassword: "Reset password",
    resendCode: "Resend code",
    backToSignIn: "Back to sign in",
    signInPrompt: "Sign in to read the Ellenberger diaries.",
    signUpPrompt:
      "Create an account to request access. Your account needs administrator approval before you can sign in.",
    pendingApprovalTitle: "Awaiting administrator approval",
    pendingApprovalBody:
      "Your email is verified but your account hasn't been approved yet. You'll get an email when it's ready.",
    verificationSentTitle: "Check your inbox",
    verificationSentBody: (email) =>
      `We sent a verification code to ${email}. Enter it below to confirm your address.`,
    newPasswordRequiredTitle: "Set a new password",
    newPasswordRequiredBody:
      "You've signed in with a temporary password. Pick a permanent one to continue.",
    forgotPasswordPrompt:
      "Enter your email and we'll send a verification code to reset your password.",
    forgotPasswordCodeSent:
      "If that email is registered, we sent a verification code. Enter it below with your new password.",
    passwordChangedTitle: "Password updated",
    passwordChangedBody: "Sign in with your new password.",
    passwordMinHint: "At least 10 characters, with upper- and lowercase letters and a digit.",
    workingLabel: "Working…",
    signedInAs: (email) => `Signed in as ${email}`,
  },
  de: {
    about: "Über",
    translationLabel: "Übersetzung:",
    diariesTitle: "Tagebücher von Henri F. Ellenberger",
    diariesSubtitle:
      "41 handschriftliche Bände von 1940 bis 1989, in Esperanto und Französisch (mit gelegentlichem Deutsch). Digitalisiert von der Österreichischen Nationalbibliothek, transkribiert und übersetzt mit Gemini.",
    loadingDiaries: "Tagebücher werden geladen…",
    loadingDiary: () => "Tagebuch wird geladen…",
    loadingPage: (n) => `Seite ${n} wird geladen…`,
    pagesCount: (n) => `${n} Seiten`,
    noThumbnail: "kein Vorschaubild",
    noTranscription: "Keine Transkription.",
    noTranslation: "Noch keine Übersetzung.",
    originalIsInTargetLanguage: (lang) => `Das Original ist in ${lang} — siehe Reiter „Original“.`,
    translationFailed: (err) => `Übersetzung fehlgeschlagen: ${err}`,
    errorPrefix: "Fehler",
    manifestLink: "IIIF-Manifest ↗",
    originalTab: "Original",
    confidenceLabel: "Transkriptionsgenauigkeit",
    msPageLabel: "Handschr. Seite",
    prev: "Zurück",
    next: "Weiter",
    pageLocator: (slug) => `Tagebuch ${slug}, Seite`,
    pageOf: (total) => `von ${total}`,
    go: "Los",
    attribution:
      "Bild bereitgestellt von der Österreichischen Nationalbibliothek, Sammlung für Plansprachen.",
    attributionDisclaimer:
      "Transkriptionen und Übersetzungen sind maschinell erstellt und können Fehler enthalten.",
    aboutTitle: "Über dieses Projekt",
    aboutIntro:
      "Heredajo (Esperanto: heredaĵo, „Erbe“) präsentiert die handschriftlichen Tagebücher von Henri F. Ellenberger (1905–1993), schweizerisch-kanadischer Psychiater und Historiker der Psychiatrie, zusammen mit maschinell erstellten Transkriptionen und Übersetzungen.",
    aboutDigitizedBy:
      "Die 41 Tagebücher umspannen die Jahre 1940 bis 1989 und sind hauptsächlich in Esperanto und Französisch verfasst, mit gelegentlichem Deutsch. Sie wurden von der Österreichischen Nationalbibliothek, Sammlung für Plansprachen, digitalisiert und werden über deren IIIF-Endpunkte bereitgestellt. Die Originalbilder auf dieser Seite werden direkt von onb.ac.at gestreamt.",
    aboutQualityNote:
      "Transkriptionen und Übersetzungen wurden mit Gemini erstellt und können Fehler enthalten — insbesondere bei kursiver Handschrift, eigenwilligem Esperanto und Seiten mit Durchstreichungen oder eingeklebtem Material. Konfidenzbewertungen und Sprachidentifikationen pro Seite spiegeln die Selbsteinschätzung des Modells wider.",
    aboutAttributionFooter:
      "Bildnachweis: Österreichische Nationalbibliothek, Sammlung für Plansprachen.",
    markCrossedOut: "vom Autor durchgestrichen",
    markIllegible: "unleserlich",
    markUncertain: "unsichere Lesung",
    markInserted: "vom Autor zwischen den Zeilen eingefügt",
    markArrow: "vom Autor mit einem Pfeil markiert",
    markDrawing: "Zeichnung auf der Seite",
    authorPageLabel: "Autor",
    signInTab: "Anmelden",
    signUpTab: "Konto erstellen",
    signIn: "Anmelden",
    signUp: "Konto erstellen",
    signOut: "Abmelden",
    email: "E-Mail",
    password: "Passwort",
    passwordConfirm: "Passwort bestätigen",
    newPassword: "Neues Passwort",
    verificationCode: "Bestätigungscode",
    forgotPassword: "Passwort vergessen?",
    resetPassword: "Passwort zurücksetzen",
    resendCode: "Code erneut senden",
    backToSignIn: "Zurück zur Anmeldung",
    signInPrompt: "Melden Sie sich an, um die Ellenberger-Tagebücher zu lesen.",
    signUpPrompt:
      "Erstellen Sie ein Konto, um Zugang anzufragen. Ihr Konto muss vom Administrator freigegeben werden, bevor Sie sich anmelden können.",
    pendingApprovalTitle: "Warten auf Freigabe",
    pendingApprovalBody:
      "Ihre E-Mail ist bestätigt, aber Ihr Konto wurde noch nicht freigegeben. Sie erhalten eine E-Mail, sobald es soweit ist.",
    verificationSentTitle: "Postfach prüfen",
    verificationSentBody: (email) =>
      `Wir haben einen Bestätigungscode an ${email} gesendet. Geben Sie ihn unten ein, um Ihre Adresse zu bestätigen.`,
    newPasswordRequiredTitle: "Neues Passwort festlegen",
    newPasswordRequiredBody:
      "Sie haben sich mit einem temporären Passwort angemeldet. Wählen Sie ein dauerhaftes, um fortzufahren.",
    forgotPasswordPrompt:
      "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Bestätigungscode zum Zurücksetzen des Passworts.",
    forgotPasswordCodeSent:
      "Falls diese Adresse registriert ist, haben wir einen Bestätigungscode gesendet. Geben Sie ihn mit Ihrem neuen Passwort ein.",
    passwordChangedTitle: "Passwort aktualisiert",
    passwordChangedBody: "Melden Sie sich mit Ihrem neuen Passwort an.",
    passwordMinHint: "Mindestens 10 Zeichen mit Groß- und Kleinbuchstaben sowie einer Ziffer.",
    workingLabel: "Verarbeite…",
    signedInAs: (email) => `Angemeldet als ${email}`,
  },
  fr: {
    about: "À propos",
    translationLabel: "Traduction :",
    diariesTitle: "Journaux d'Henri F. Ellenberger",
    diariesSubtitle:
      "41 volumes manuscrits couvrant 1940–1989, rédigés en espéranto et en français (avec quelques passages en allemand). Numérisés par la Bibliothèque nationale d'Autriche, transcrits et traduits avec Gemini.",
    loadingDiaries: "Chargement des journaux…",
    loadingDiary: () => "Chargement du journal…",
    loadingPage: (n) => `Chargement de la page ${n}…`,
    pagesCount: (n) => `${n} pages`,
    noThumbnail: "aucune vignette",
    noTranscription: "Aucune transcription.",
    noTranslation: "Pas encore de traduction.",
    originalIsInTargetLanguage: (lang) => `L'original est en ${lang} — voir l'onglet Original.`,
    translationFailed: (err) => `Échec de la traduction : ${err}`,
    errorPrefix: "Erreur",
    manifestLink: "Manifeste IIIF ↗",
    originalTab: "Original",
    confidenceLabel: "Confiance de la transcription",
    msPageLabel: "Page MS",
    prev: "Préc.",
    next: "Suiv.",
    pageLocator: (slug) => `Journal ${slug}, page`,
    pageOf: (total) => `sur ${total}`,
    go: "Aller",
    attribution:
      "Image fournie par la Bibliothèque nationale d'Autriche (Österreichische Nationalbibliothek), Département des langues planifiées.",
    attributionDisclaimer:
      "Les transcriptions et traductions sont générées par machine et peuvent contenir des erreurs.",
    aboutTitle: "À propos de ce projet",
    aboutIntro:
      "Heredajo (espéranto : heredaĵo, « héritage ») présente les journaux manuscrits d'Henri F. Ellenberger (1905–1993), psychiatre et historien de la psychiatrie suisso-canadien, accompagnés de transcriptions et de traductions générées par machine.",
    aboutDigitizedBy:
      "Les 41 journaux couvrent la période 1940–1989 et sont rédigés principalement en espéranto et en français, avec quelques passages en allemand. Ils ont été numérisés par la Bibliothèque nationale d'Autriche, Département des langues planifiées, et sont servis via ses points de terminaison IIIF. Les images originales sur ce site sont diffusées directement depuis onb.ac.at.",
    aboutQualityNote:
      "Les transcriptions et traductions ont été produites avec Gemini et peuvent contenir des erreurs — en particulier avec l'écriture cursive, l'espéranto idiosyncratique et les pages comportant du texte barré ou collé. Les niveaux de confiance et les langues détectées sur chaque page reflètent l'auto-évaluation du modèle.",
    aboutAttributionFooter:
      "Crédit image : Österreichische Nationalbibliothek, Sammlung für Plansprachen.",
    markCrossedOut: "barré par l'auteur",
    markIllegible: "illisible",
    markUncertain: "lecture incertaine",
    markInserted: "inséré entre les lignes par l'auteur",
    markArrow: "indiqué par une flèche de l'auteur",
    markDrawing: "dessin sur la page",
    authorPageLabel: "auteur",
    signInTab: "Se connecter",
    signUpTab: "Créer un compte",
    signIn: "Se connecter",
    signUp: "Créer un compte",
    signOut: "Se déconnecter",
    email: "Courriel",
    password: "Mot de passe",
    passwordConfirm: "Confirmer le mot de passe",
    newPassword: "Nouveau mot de passe",
    verificationCode: "Code de vérification",
    forgotPassword: "Mot de passe oublié ?",
    resetPassword: "Réinitialiser le mot de passe",
    resendCode: "Renvoyer le code",
    backToSignIn: "Retour à la connexion",
    signInPrompt: "Connectez-vous pour lire les journaux d'Ellenberger.",
    signUpPrompt:
      "Créez un compte pour demander l'accès. Votre compte doit être approuvé par l'administrateur avant que vous puissiez vous connecter.",
    pendingApprovalTitle: "En attente d'approbation",
    pendingApprovalBody:
      "Votre adresse est vérifiée mais votre compte n'a pas encore été approuvé. Vous recevrez un courriel lorsque ce sera fait.",
    verificationSentTitle: "Vérifiez votre boîte de réception",
    verificationSentBody: (email) =>
      `Nous avons envoyé un code de vérification à ${email}. Entrez-le ci-dessous pour confirmer votre adresse.`,
    newPasswordRequiredTitle: "Définir un nouveau mot de passe",
    newPasswordRequiredBody:
      "Vous vous êtes connecté avec un mot de passe temporaire. Choisissez-en un permanent pour continuer.",
    forgotPasswordPrompt:
      "Entrez votre adresse e-mail et nous vous enverrons un code de vérification pour réinitialiser votre mot de passe.",
    forgotPasswordCodeSent:
      "Si cette adresse est enregistrée, nous avons envoyé un code de vérification. Entrez-le avec votre nouveau mot de passe.",
    passwordChangedTitle: "Mot de passe mis à jour",
    passwordChangedBody: "Connectez-vous avec votre nouveau mot de passe.",
    passwordMinHint: "Au moins 10 caractères, avec majuscules, minuscules et un chiffre.",
    workingLabel: "Traitement…",
    signedInAs: (email) => `Connecté en tant que ${email}`,
  },
  eo: {
    about: "Pri",
    translationLabel: "Traduko:",
    diariesTitle: "Taglibroj de Henri F. Ellenberger",
    diariesSubtitle:
      "41 manskribitaj volumoj de 1940 ĝis 1989, verkitaj en Esperanto kaj Franca (kun foja Germana). Ciferecigitaj de la Aŭstra Nacia Biblioteko, transskribitaj kaj tradukitaj per Gemini.",
    loadingDiaries: "Ŝargas taglibrojn…",
    loadingDiary: () => "Ŝargas taglibron…",
    loadingPage: (n) => `Ŝargas paĝon ${n}…`,
    pagesCount: (n) => `${n} paĝoj`,
    noThumbnail: "neniu antaŭbildo",
    noTranscription: "Neniu transskribaĵo.",
    noTranslation: "Ankoraŭ neniu traduko.",
    originalIsInTargetLanguage: (lang) =>
      `La originalo estas en ${lang} — vidu la langeton „Originalo“.`,
    translationFailed: (err) => `Traduko malsukcesis: ${err}`,
    errorPrefix: "Eraro",
    manifestLink: "IIIF-manifesto ↗",
    originalTab: "Originalo",
    confidenceLabel: "Transskriba certeco",
    msPageLabel: "Manskr. paĝo",
    prev: "Antaŭa",
    next: "Sekva",
    pageLocator: (slug) => `Taglibro ${slug}, paĝo`,
    pageOf: (total) => `el ${total}`,
    go: "Iru",
    attribution:
      "Bildo afable disponigita de la Aŭstra Nacia Biblioteko (Österreichische Nationalbibliothek), Sekcio de Planlingvoj.",
    attributionDisclaimer:
      "Transskribaĵoj kaj tradukoj estas maŝin-generitaj kaj povas enhavi erarojn.",
    aboutTitle: "Pri ĉi tiu projekto",
    aboutIntro:
      "Heredajo (Esperante: heredaĵo) prezentas la manskribitajn taglibrojn de Henri F. Ellenberger (1905–1993), svisa-kanada psikiatro kaj historiisto de psikiatrio, kune kun maŝin-generitaj transskribaĵoj kaj tradukoj.",
    aboutDigitizedBy:
      "La 41 taglibroj kovras la jarojn 1940–1989 kaj estas verkitaj ĉefe en Esperanto kaj Franca, kun fojaj germanaj pasaĵoj. Ili estis ciferecigitaj de la Aŭstra Nacia Biblioteko, Sekcio de Planlingvoj, kaj estas servataj per ĝiaj IIIF-finpunktoj. La originalaj bildoj sur ĉi tiu retejo estas elsendataj rekte de onb.ac.at.",
    aboutQualityNote:
      "Transskribaĵoj kaj tradukoj estis produktitaj per Gemini kaj povas enhavi erarojn — speciale ĉe kursiva manskribo, idiosinkratika Esperanto, kaj paĝoj kun trastrekitaj aŭ algluitaj materialoj. Certec-noticoj kaj font-lingvaj detektoj sur ĉiu paĝo registras la mem-takson de la modelo.",
    aboutAttributionFooter:
      "Bilda atribuo: Österreichische Nationalbibliothek, Sammlung für Plansprachen.",
    markCrossedOut: "forstrekita de la aŭtoro",
    markIllegible: "nelegebla",
    markUncertain: "necerta legado",
    markInserted: "enmetita inter la linioj de la aŭtoro",
    markArrow: "indikita per sago de la aŭtoro",
    markDrawing: "desegnaĵo sur la paĝo",
    authorPageLabel: "aŭtoro",
    signInTab: "Ensaluti",
    signUpTab: "Krei konton",
    signIn: "Ensaluti",
    signUp: "Krei konton",
    signOut: "Elsaluti",
    email: "Retpoŝto",
    password: "Pasvorto",
    passwordConfirm: "Konfirmu pasvorton",
    newPassword: "Nova pasvorto",
    verificationCode: "Konfirmkodo",
    forgotPassword: "Forgesis pasvorton?",
    resetPassword: "Restarigi pasvorton",
    resendCode: "Resendi kodon",
    backToSignIn: "Reen al ensaluto",
    signInPrompt: "Ensalutu por legi la taglibrojn de Ellenberger.",
    signUpPrompt:
      "Kreu konton por peti aliron. Via konto bezonas administran aprobon antaŭ ol vi povos ensaluti.",
    pendingApprovalTitle: "Atendas administran aprobon",
    pendingApprovalBody:
      "Via retpoŝto estas konfirmita, sed via konto ankoraŭ ne estas aprobita. Vi ricevos retmesaĝon kiam ĝi estos preta.",
    verificationSentTitle: "Kontrolu vian retpoŝton",
    verificationSentBody: (email) =>
      `Ni sendis konfirmkodon al ${email}. Enmetu ĝin sube por konfirmi vian adreson.`,
    newPasswordRequiredTitle: "Difinu novan pasvorton",
    newPasswordRequiredBody: "Vi ensalutis per provizora pasvorto. Elektu konstantan por daŭrigi.",
    forgotPasswordPrompt:
      "Enmetu vian retpoŝton kaj ni sendos konfirmkodon por restarigi vian pasvorton.",
    forgotPasswordCodeSent:
      "Se tiu adreso estas registrita, ni sendis konfirmkodon. Enmetu ĝin kun via nova pasvorto.",
    passwordChangedTitle: "Pasvorto ĝisdatigita",
    passwordChangedBody: "Ensalutu per via nova pasvorto.",
    passwordMinHint: "Almenaŭ 10 signoj, kun majuskloj, minuskloj kaj cifero.",
    workingLabel: "Prilaboras…",
    signedInAs: (email) => `Ensalutinta kiel ${email}`,
  },
};

export function t(lang: Language): Strings {
  return STRINGS[lang];
}
